import { Check, Link2 } from 'lucide-react';
import { memo, useEffect, useId, useRef, useState } from 'react';
import { WhatsAppButton } from '@/components/product/WhatsAppButton';
import { AppLink } from '@/components/ui/AppLink';
import { Button } from '@/components/ui/Button';
import { buttonClasses } from '@/components/ui/button-classes';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import type { VisualizerCopy } from './content';
import { copyText } from './copy-link';

export interface ShareActionsProps {
  /** The stone to enquire about; `null` while the catalogue cannot say (a general enquiry then). */
  product: { slug: string; name: string; sku: string } | null;
  /** The absolute address of this exact view. */
  link: string;
  copy: VisualizerCopy['actions'];
  /** Said politely once after a successful copy. */
  copiedMessage: string;
  onAnnounce: (message: string) => void;
}

type CopyState = 'idle' | 'copied' | 'manual';

/**
 * What to do with a view you like: enquire (with the stone pre-filled), chat on WhatsApp, copy the link.
 * Copying uses the clipboard and confirms in the page's live region; where the clipboard is not
 * available the link is shown selected, ready to copy by hand.
 */
export const ShareActions = memo(function ShareActions({
  product,
  link,
  copy,
  copiedMessage,
  onAnnounce,
}: ShareActionsProps) {
  const [state, setState] = useState<CopyState>('idle');
  const fieldId = useId();
  const field = useRef<HTMLInputElement | null>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(
    () => () => {
      window.clearTimeout(timer.current);
    },
    [],
  );

  // After a failed copy the field appears — put the cursor in it with everything selected.
  useEffect(() => {
    if (state === 'manual') field.current?.select();
  }, [state]);

  const copyLink = async (): Promise<void> => {
    const result = await copyText(link);
    window.clearTimeout(timer.current);
    if (result === 'copied') {
      setState('copied');
      onAnnounce(copiedMessage);
      timer.current = window.setTimeout(() => {
        setState('idle');
      }, 2600);
    } else {
      setState('manual');
    }
  };

  return (
    <div className="viz-actions">
      <AppLink
        to={{
          pathname: '/contact-us',
          ...(product ? { search: `?product=${product.slug}` } : {}),
          hash: '#enquiry',
        }}
        className={buttonClasses({
          variant: 'primary',
          size: 'lg',
          className: 'viz-actions__enquire',
        })}
      >
        {copy.enquire}
      </AppLink>

      <div className="viz-actions__row">
        {product ? <WhatsAppButton size="lg" product={product} /> : <WhatsAppButton size="lg" />}
        <Button
          variant="glass"
          size="lg"
          className="viz-actions__copy"
          onClick={() => {
            void copyLink();
          }}
        >
          {state === 'copied' ? (
            <Check size={18} aria-hidden="true" />
          ) : (
            <Link2 size={18} aria-hidden="true" />
          )}
          {state === 'copied' ? copy.copied : copy.copy}
        </Button>
      </div>

      {state === 'manual' ? (
        <div className="viz-manual">
          <Label htmlFor={fieldId}>{copy.manualLabel}</Label>
          <Input
            id={fieldId}
            ref={field}
            readOnly
            value={link}
            aria-describedby={`${fieldId}-hint`}
            onFocus={(event) => {
              event.currentTarget.select();
            }}
          />
          <p id={`${fieldId}-hint`} className="t-small text-fg-muted">
            {copy.manualHint}
          </p>
        </div>
      ) : null}
    </div>
  );
});
